import React from 'react';
import { CustomStepProps } from '../../types/survey';

export const FeelingsAssessmentResults: React.FC<CustomStepProps> = ({
  onNext,
  onBack,
  surveyData,
}) => {
  // Calculate feelings assessment score based on user's answers
  const calculateFeelingsScore = () => {
    let totalScore = 0;
    const answers = surveyData?.answers || {};

    // Page 9 questions
    // Lack of purpose (reverse scoring - disagree is better)
    const purpose = answers.purpose;
    if (purpose === 1) totalScore += 5; // Strongly disagree
    else if (purpose === 2) totalScore += 4; // Disagree
    else if (purpose === 3) totalScore += 3; // Neutral
    else if (purpose === 4) totalScore += 2; // Agree
    else if (purpose === 5) totalScore += 1; // Strongly agree

    // Fear of not taking care of self (reverse scoring)
    const careForSelf = answers.care_for_self;
    if (careForSelf === 1) totalScore += 5; // Strongly disagree
    else if (careForSelf === 2) totalScore += 4; // Disagree
    else if (careForSelf === 3) totalScore += 3; // Neutral
    else if (careForSelf === 4) totalScore += 2; // Agree
    else if (careForSelf === 5) totalScore += 1; // Strongly agree

    // Importance of improvement (positive scoring)
    const improvement = answers.importance_of_improvement;
    if (improvement) totalScore += improvement;

    // Support availability (positive scoring)
    const support = answers.support;
    if (support) totalScore += support;

    // Page 10 questions
    // Problem dealing (positive scoring)
    const problemDealing = answers.problem_dealing;
    if (problemDealing) totalScore += problemDealing;

    // Explosive reactions (reverse scoring)
    const explosiveReactions = answers.explosive_reactions;
    if (explosiveReactions === 1) totalScore += 5; // Strongly disagree
    else if (explosiveReactions === 2) totalScore += 4; // Disagree
    else if (explosiveReactions === 3) totalScore += 3; // Neutral
    else if (explosiveReactions === 4) totalScore += 2; // Agree
    else if (explosiveReactions === 5) totalScore += 1; // Strongly agree

    // Boredom (reverse scoring)
    const boredom = answers.boredom;
    if (boredom === 1) totalScore += 5; // Strongly disagree
    else if (boredom === 2) totalScore += 4; // Disagree
    else if (boredom === 3) totalScore += 3; // Neutral
    else if (boredom === 4) totalScore += 2; // Agree
    else if (boredom === 5) totalScore += 1; // Strongly agree

    // Breakdown (reverse scoring)
    const breakdown = answers.breakdown;
    if (breakdown === 1) totalScore += 5; // Strongly disagree
    else if (breakdown === 2) totalScore += 4; // Disagree
    else if (breakdown === 3) totalScore += 3; // Neutral
    else if (breakdown === 4) totalScore += 2; // Agree
    else if (breakdown === 5) totalScore += 1; // Strongly agree

    return totalScore;
  };

  const score = calculateFeelingsScore();
  const needsSupport = score < 24; // Less than 60% of max score (40)

  // Get mental wellness level description
  const getWellnessLevelText = () => {
    if (score < 16) return "Needs Immediate Support"; // Less than 40%
    if (score < 24) return "Room for Improvement"; // Less than 60%
    if (score < 32) return "Good Mental Health"; // Less than 80%
    return "Excellent Mental Wellness"; // 80% or more
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-royal-blue-lightest via-white to-royal-gold-lightest flex items-center justify-center p-4">
      <div className="max-w-3xl w-full">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section with Gradient */}
          <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/30 rounded-full mb-4">
                <svg className="w-10 h-10 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h1 className="text-4xl font-bold mb-4 text-gray-800">
                Mental Wellness Assessment
              </h1>
              <p className="text-xl text-gray-700">
                Your emotional and mental health analysis
              </p>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            {/* Score Display */}
            <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest rounded-2xl p-6 mb-8">
              <div className="text-center">
                <div className="text-5xl font-bold mb-2 text-gray-800">{score}</div>
                <div className="text-lg text-gray-700">Mental Wellness Score</div>
                <div className="text-sm text-gray-600 mt-2">{getWellnessLevelText()}</div>
              </div>
            </div>

            {/* Main Message Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Understanding Mental Health
              </h2>
              <div className="bg-gradient-to-r from-royal-gold-lightest to-royal-blue-lightest rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Mental health consists of three factors: a good health condition, a sense of mental comfort and 
                  maintaining proper social relations. Mental health is a state of balance between body and mind.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  When we are in good mental health, we cope better with difficult situations in both our personal 
                  and professional lives. Most importantly, our mental health also helps to strengthen our immunity!
                </p>
              </div>
            </div>

            {/* Three Pillars of Mental Health */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                The Three Pillars of Mental Health
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-royal-gold-lightest rounded-lg p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-royal-gold/20 rounded-full mb-4">
                    <svg className="w-8 h-8 text-royal-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Physical Health</h3>
                  <p className="text-sm text-gray-600">
                    A healthy body supports a healthy mind through proper nutrition, exercise, and rest
                  </p>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-royal-blue/20 rounded-full mb-4">
                    <svg className="w-8 h-8 text-royal-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Mental Comfort</h3>
                  <p className="text-sm text-gray-600">
                    Inner peace, emotional stability, and psychological well-being
                  </p>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-royal-gold/20 rounded-full mb-4">
                    <svg className="w-8 h-8 text-royal-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Social Relations</h3>
                  <p className="text-sm text-gray-600">
                    Meaningful connections and healthy relationships with others
                  </p>
                </div>
              </div>
            </div>

            {/* Support Message */}
            {needsSupport && (
              <div className="mb-8">
                <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-amber-800">We're Here to Help</h3>
                      <p className="mt-2 text-sm text-amber-700">
                        If you don't know how to cope with your problems, you don't feel happy, everyday life exhausts you, 
                        you don't find any sense in what you are doing or you don't feel significant bonds with others, 
                        contact us – Longevity Center experts will provide you with professional help.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recommendations */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Our Experts Recommend
              </h2>
              <div className="space-y-3">
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-gold mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Practice mindfulness</span> - Even 10 minutes of daily meditation can reduce stress
                    </p>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-blue mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Stay connected</span> - Maintain regular contact with friends and family
                    </p>
                  </div>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-gold mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Get adequate sleep</span> - 7-9 hours of quality sleep supports emotional balance
                    </p>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-blue mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Exercise regularly</span> - Physical activity boosts mood-enhancing endorphins
                    </p>
                  </div>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-gold mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Seek professional help</span> - There's no shame in talking to a mental health expert
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mental Health Benefits */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Benefits of Good Mental Health</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Better Resilience</span>
                      <p className="text-xs text-gray-600 mt-1">Cope with life's challenges</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Stronger Immunity</span>
                      <p className="text-xs text-gray-600 mt-1">Mental health affects physical health</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Improved Relationships</span>
                      <p className="text-xs text-gray-600 mt-1">Better social connections</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Greater Productivity</span>
                      <p className="text-xs text-gray-600 mt-1">Enhanced focus and creativity</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8">
              <button
                onClick={onBack}
                className="px-8 py-3 bg-white border-2 border-royal-gold text-royal-gold font-semibold rounded-lg hover:bg-royal-gold hover:text-white transition-colors"
              >
                Previous
              </button>
              <button
                onClick={onNext}
                className="px-8 py-3 bg-royal-gold text-white font-semibold rounded-lg hover:bg-royal-gold-dark transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};